// **The C-1 snapshot → renderer input adapter** (`demo-presentation-shell` task 6.4, design
// **DD4**).
//
// One pure module, and `CanvasRenderer` is not modified — the same argument that closed **D-156**
// for the review screen and produced `ui/home/ambient-world.ts` for the ambient loop. The stage
// draws through the shipped renderer, fed the pair it already takes: a `WorldSnapshot`
// (`net/wire/connection.ts`) plus a `RenderInput` (`canvas/render-input.ts`).
//
// ## What this adapter refuses to do
//
// **It supplies no facing and no gait.** The shipped `FacingDeriver` derives heading from
// successive positions, which is why C-1 carries neither; `BeeRecord.facing` is structurally
// required and set to the wire's own constant `0`, exactly as `ambient-world.ts` sets it.
//
// **It re-derives no gather signal.** `BeeRecord.gathering` is carried from C-1's own receipt
// flag. `ActionClass.Forage` collapses eight distinct actions, which is the mistake
// `wire-gathering-bit` exists to prevent.
//
// **It defaults nothing.** A missing vitals field, a missing cell hp bucket, an unrecognised
// action: each is a {@link DemoSnapshotError} naming the field, never a zero. A defaulted field
// produces a world that draws successfully and is wrong — every bee at full health, every cell
// undamaged — which is precisely the failure a presentation cannot detect on stage.
//
// ## A cross-lane note on where the per-bee maxima live
//
// This change's own delta spec describes the adapter as *quantizing* per-bee vitals against maxima
// the snapshot carries. The C-1 contract landed the other way round: the programme page
// (`docs/dev/plans/demo/presentation-mode.md` §C-1) and `demo-stage-runtime`'s render-snapshot
// requirement both pin `health`/`nectar` as **0–255 fractions of that bee's own maxima with the
// raw maxima deliberately not carried beside them** ("one representation or the other, never
// both"), which design **DD4** explicitly permits ("in either form — renderer-ready values or
// raw-plus-maxima… written to consume what C-1 provides"). So the invariant the spec is protecting
// — a ×2-buffed bee at full reads 255 exactly as a base bee does, and nothing is ever divided by a
// balance constant, a colony maximum or a hard-coded cap — holds here by *carrying* the fraction
// and refusing anything outside its domain, rather than by dividing. Task 13.3 is where this is
// checked against the committed `fixtures/demo-c1/` goldens.

import type { WorldSnapshot } from '../../net/wire/connection';
import type { BeeRecord, CellRecord, PatchRecord } from '../../net/wire/messages';
import { ActionClass, Season, Tier } from '../../net/wire/messages';
import type { RenderInput } from '../../canvas/render-input';
import type { DemoInstructWorldRead } from './stage-driver';

/** A refusal naming the field that was absent, out of domain, or unrecognised. */
export class DemoSnapshotError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DemoSnapshotError';
  }
}

export interface DemoSnapshotOptions {
  /** The presenter's colony — what gives its bees their type colours rather than the enemy band,
   * and what anchors the hive and the queen. */
  ownColonyId: number;
}

export interface AdaptedDemoSnapshot {
  world: WorldSnapshot;
  renderInput: RenderInput;
}

/** C-1's action classes, verbatim — the shipped `ActionClass` values under their wire names. */
const ACTION_CLASSES: Readonly<Record<string, ActionClass>> = {
  forage: ActionClass.Forage,
  build: ActionClass.Build,
  fight: ActionClass.Fight,
  idle: ActionClass.Idle,
};

/** The renderer's raw hp bucket domain (`CellRecord.hpBucket`, state byte bits 3–7). */
const HP_BUCKET_MAX = 31;
/** The quantized-fraction domain shared by `BeeRecord.health`/`.nectar` and `PatchRecord.pool`. */
const FRACTION_MAX = 255;

function asRecord(value: unknown, at: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new DemoSnapshotError(`the demo snapshot's ${at} is not an object`);
  }
  return value as Record<string, unknown>;
}

function asArray(source: Record<string, unknown>, key: string): unknown[] {
  const value = source[key];
  if (value === undefined) {
    throw new DemoSnapshotError(`the demo snapshot carries no ${key}`);
  }
  if (!Array.isArray(value)) {
    throw new DemoSnapshotError(`the demo snapshot's ${key} is not an array`);
  }
  return value;
}

function num(source: Record<string, unknown>, key: string, at: string): number {
  const value = source[key];
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new DemoSnapshotError(`the demo snapshot's ${at}.${key} is missing or not a number`);
  }
  return value;
}

/** A number that must sit inside a closed domain. Out of range is a refusal, never a clamp: a
 * clamp is how a raw hit-point count silently becomes "full health". */
function bounded(
  source: Record<string, unknown>,
  key: string,
  at: string,
  max: number,
): number {
  const value = num(source, key, at);
  if (value < 0 || value > max) {
    throw new DemoSnapshotError(`the demo snapshot's ${at}.${key} is ${value}, outside 0..${max}`);
  }
  return value;
}

function bool(source: Record<string, unknown>, key: string, at: string): boolean {
  const value = source[key];
  if (typeof value !== 'boolean') {
    throw new DemoSnapshotError(`the demo snapshot's ${at}.${key} is missing or not a boolean`);
  }
  return value;
}

function point(source: Record<string, unknown>, at: string): [number, number] {
  return [num(source, 'x', at), num(source, 'y', at)];
}

/**
 * Converts one C-1 render snapshot — the JSON string the worker posts, or its parsed form — into
 * the shipped renderer's own inputs.
 *
 * Throws {@link DemoSnapshotError}, naming the offending field, rather than producing a
 * partially-defaulted world.
 */
export function adaptDemoSnapshot(
  raw: unknown,
  options: DemoSnapshotOptions,
): AdaptedDemoSnapshot {
  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      throw new DemoSnapshotError('the demo snapshot is not valid JSON');
    }
  }
  const source = asRecord(parsed, 'body');
  // C-1 answers refusals as values on the same envelope. A refusal drawn as an empty world would
  // clear the stage in front of the audience and say nothing.
  if (source.status === 'refused') {
    throw new DemoSnapshotError(
      `the demo run refused the step: ${String(source.reason ?? 'unknown')}`,
    );
  }

  const tick = num(source, 'tick', 'snapshot');

  // Colonies first: a cell's `ruin` and the render input's lifecycle are both facts about the
  // owning colony, not about the cell or the frame.
  const aliveByColony = new Map<number, boolean>();
  const hiveByColony = new Map<number, [number, number]>();
  asArray(source, 'colonies').forEach((entry, index) => {
    const at = `colonies[${index}]`;
    const colony = asRecord(entry, at);
    const id = num(colony, 'id', at);
    aliveByColony.set(id, bool(colony, 'alive', at));
    hiveByColony.set(id, point(asRecord(colony.hive, `${at}.hive`), `${at}.hive`));
  });

  const bees = new Map<number, BeeRecord>();
  // The own queen's live position, when the snapshot carries her. She is drawn from the
  // `own-queen` anchor (below), never as a `bee` — the D-091 exclusion `review-world.ts` already
  // makes. Before it, the renderer drew her TWICE: the static anchor sprite pinned at the hive
  // point plus this live simulated bee, which read on stage as "two purple bees" (playthrough
  // finding, 2026-08-09). Enemy queens have no anchor and stay excluded too — the demo narrates
  // exactly one queen.
  let ownQueenAt: [number, number] | null = null;
  asArray(source, 'bees').forEach((entry, index) => {
    const at = `bees[${index}]`;
    const bee = asRecord(entry, at);
    const actionName = bee.action;
    if (typeof actionName !== 'string' || !(actionName in ACTION_CLASSES)) {
      throw new DemoSnapshotError(
        `the demo snapshot's ${at}.action is ${JSON.stringify(actionName)}, which is not a renderer action class`,
      );
    }
    const id = num(bee, 'id', at);
    const typeId = bee.type;
    if (typeId === undefined || typeId === null) {
      // A queen (the only typeless bee). The own colony's is the anchor's subject; any other
      // colony's is simply not rendered.
      if (num(bee, 'colony', at) === options.ownColonyId) {
        ownQueenAt = point(bee, at) as [number, number];
      }
      return;
    }
    bees.set(id, {
      id,
      position: point(bee, at),
      // Supplied as the wire's own constant, never derived here: the renderer's `FacingDeriver`
      // owns heading, from successive positions.
      facing: 0,
      action: ACTION_CLASSES[actionName]!,
      gathering: bool(bee, 'gathering', at),
      colonyId: num(bee, 'colony', at),
      typeId: num(bee, 'type', at),
      health: bounded(bee, 'health', at, FRACTION_MAX),
      nectar: bounded(bee, 'nectar', at, FRACTION_MAX),
    });
  });

  const cells = new Map<number, CellRecord>();
  asArray(source, 'cells').forEach((entry, index) => {
    const at = `cells[${index}]`;
    const cell = asRecord(entry, at);
    const id = num(cell, 'id', at);
    const colonyId = num(cell, 'colony', at);
    cells.set(id, {
      id,
      position: point(cell, at),
      occupied: bool(cell, 'occupied', at),
      damaged: bool(cell, 'damaged', at),
      // A cell of a dead colony is a neutral ruin whatever its own flag says (DESIGN §2) — the
      // colony's alive flag is the authority, which is why it is folded in here rather than
      // trusted from the cell.
      ruin: bool(cell, 'ruin', at) || aliveByColony.get(colonyId) === false,
      hpBucket: bounded(cell, 'hpBucket', at, HP_BUCKET_MAX),
    });
  });

  const patches = new Map<number, PatchRecord>();
  asArray(source, 'patches').forEach((entry, index) => {
    const at = `patches[${index}]`;
    const patch = asRecord(entry, at);
    const id = num(patch, 'id', at);
    const pool = bounded(patch, 'pool', at, FRACTION_MAX);
    patches.set(id, {
      id,
      position: point(patch, at),
      // `pool > 0` is exactly the live-versus-dead flower predicate the renderer reads, so a
      // patch locked out recovering (WAG-13's rebloom threshold) draws as unworkable rather than
      // in bloom with nectar nobody can take.
      pool: bool(patch, 'recovering', at) ? 0 : pool,
    });
  });

  const ownAlive = aliveByColony.get(options.ownColonyId) ?? true;

  return {
    world: {
      tier: Tier.Close,
      tick,
      seq: BigInt(tick),
      bees,
      // Wide-tier drawables, and the stage is never at the wide tier: blobs and queens are
      // aggregates the demo world does not produce (its queen arrives as a bee with no type), and
      // `ruins` is the wide tier's own dead-colony marker — at the close tier a dead colony's
      // remains are its cells, carrying `ruin` above.
      blobs: new Map(),
      queens: new Map(),
      patches,
      ruins: new Map(),
      // C-1 carries no dances, so there is nothing for a watcher radius to derive over — the same
      // `0` `ambient-world.ts` supplies for the same reason.
      dances: new Map(),
      danceRadius: 0,
      cells,
      // A local world is never viewport-capped: reporting a truncation that did not happen would
      // drop the renderer into `reduced` animation for no reason.
      cellsTruncated: false,
      truncated: false,
      totalCount: bees.size,
    },
    renderInput: {
      // The stage is a summer field; the demo never runs the season down.
      season: Season.Summer,
      // The anchor is where the renderer draws the `own-queen` sprite — pinned to the QUEEN's own
      // simulated position rather than the hive point, so the one rendered queen is the moving
      // one, with her proper sprite. The hive tile the anchor also carries is filtered out by the
      // renderer whenever cells exist, and every staged chapter has cells, so nothing else moves
      // with her.
      ownHiveAnchor: ownQueenAt ?? hiveByColony.get(options.ownColonyId) ?? null,
      ownColonyLifecycle: ownAlive ? 'live' : 'eliminated',
      ownColonyId: options.ownColonyId,
      // Live play, not replay: there is no playhead.
      playhead: null,
      connectionEpoch: 0,
      streamEpoch: 0,
    },
  };
}

// -- the instruct chapter's world read ---------------------------------------------------------
//
// **A second adaptation of the same snapshot, and deliberately in the same file.** `POST
// /demo/instruct` is built from the roster, hive and patches of the *local* wasm world (spec "The
// instruct prompt surface is single-in-flight, idempotent and stage-safe"), which is the same C-1
// snapshot the renderer draws — a different projection of it, not a different source. It lives
// beside {@link adaptDemoSnapshot} so the worker (production) and `test/fake-demo-stage-driver.ts`
// (the C-1 double) read the wire through one function rather than two copies, which is the failure
// mode task 13.3 already paid for once with `stage-json.ts`.
//
// Unlike the renderer's adapter this one **degrades rather than refuses**: an empty roster costs
// the agent context, whereas a thrown error costs the presenter the beat, and the instruct surface
// has no way to show a stack trace to a room.

/** What the snapshot itself cannot say: the field's extent (C-1 reports it at configure) and the
 * colony's type **names** (the wire carries only the registration index). */
export interface InstructWorldContext {
  fieldSizeU: number;
  /** Colony 0's type names, indexed by the wire's numeric `type`. */
  typeNames: readonly string[];
}

interface InstructSnapshotShape {
  bees?: { id: number; colony: number; type?: number | null; x: number; y: number; nectar: number }[];
  colonies?: { id: number; hive?: { x: number; y: number } }[];
  patches?: { id: number; x: number; y: number; pool: number; recovering: boolean }[];
}

/** The presenter's own colony — the same convention every other demo module fixes. */
const OWN_COLONY = 0;

export function deriveInstructWorld(raw: unknown, context: InstructWorldContext): DemoInstructWorldRead {
  let parsed: InstructSnapshotShape;
  try {
    parsed = (typeof raw === 'string' ? JSON.parse(raw) : raw) as InstructSnapshotShape;
  } catch {
    parsed = {};
  }
  const own = (parsed.colonies ?? []).find((colony) => colony.id === OWN_COLONY);
  return {
    fieldSizeU: context.fieldSizeU,
    hive: own?.hive ?? { x: 0, y: 0 },
    roster: (parsed.bees ?? [])
      // The queen carries no type and is not addressable by name — the same exclusion the
      // colony-wide `resume` makes, for the same reason.
      .filter((bee) => bee.colony === OWN_COLONY && bee.type !== undefined && bee.type !== null)
      .map((bee) => ({
        id: bee.id,
        // Names, never the numeric wire id: the instruct endpoint's vocabulary is name-scoped
        // exactly as B++ itself is.
        type: context.typeNames[bee.type as number] ?? `type-${bee.type}`,
        x: bee.x,
        y: bee.y,
        carrying: bee.nectar > 0,
      })),
    patches: (parsed.patches ?? []).map((patch) => ({
      id: patch.id,
      x: patch.x,
      y: patch.y,
      hasNectar: patch.pool > 0 && !patch.recovering,
    })),
  };
}
